import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { projectStatusLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatMoney } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { useCurrencies } from './Clients';
import { SubscriptionStatusBadge, useClientName, usePeriodPrice, useSubscriptionProjects, type Cycle, type SubscriptionStatus } from './Subscriptions';

/**
 * What Finance sells by subscription (2026-10-28): the internal projects —
 * projects with no client, made in the admin — it offers, and each one's
 * packages with their list prices. A list price is only where a new
 * subscription starts: changing it changes nothing agreed.
 *
 * Only the projects chosen to be sold are listed (operator, 2026-10-28: "show
 * what i chose only"); another is chosen on its own page, Offer a project.
 */

interface PackageRow {
  id: string;
  projectId: string;
  project: { id: string; code: string; name: string; nameAr: string | null };
  code: string;
  name: string;
  nameAr: string | null;
  description: string | null;
  currency: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  isActive: boolean;
  archivedAt: string | null;
  liveSubscriptions: number;
}

interface PackageDetail extends PackageRow {
  projectOffered: boolean;
  everSubscribed: number;
  subscribers: Array<{
    id: string;
    number: string;
    status: SubscriptionStatus;
    billingCycle: Cycle;
    currency: string;
    netPrice: number;
    nextBillingDate: string | null;
    cancelAtPeriodEnd: boolean;
    client: { id: string; code: string; companyName: string; companyNameAr: string | null };
  }>;
  can: { changeCode: boolean; changeCurrency: boolean; archive: boolean; restore: boolean };
}

function ListPrices({ pkg }: { pkg: Pick<PackageRow, 'monthlyPrice' | 'yearlyPrice' | 'currency'> }) {
  const { t, locale } = useI18n();
  const price = (value: number | null, per: TranslationKey) =>
    value === null ? null : `${formatMoney(value, pkg.currency, locale)} ${t(per)}`;
  return (
    <span className="tabular">
      {[price(pkg.monthlyPrice, 'subs.per.MONTHLY'), price(pkg.yearlyPrice, 'subs.per.YEARLY')].filter(Boolean).join(' · ')}
    </span>
  );
}

export function PackagesPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [archived, setArchived] = useState(false);
  const projects = useSubscriptionProjects();
  const offered = (projects.data ?? []).filter((p) => p.offered);
  const offerable = (projects.data ?? []).some((p) => !p.offered && !p.closed);
  // A link may still name one no longer offered — a package's back link does.
  const selectedId = params.get('projectId') ?? offered[0]?.id ?? '';
  const selected = projects.data?.find((p) => p.id === selectedId);

  const packages = useQuery({
    queryKey: ['subscription-packages', selectedId, archived],
    queryFn: () => api.get<PackageRow[]>('/subscription-packages', { projectId: selectedId, includeArchived: archived ? 'true' : 'false' }),
    enabled: !!selectedId,
  });

  const toggle = useMutation({
    mutationFn: ({ projectId, offer }: { projectId: string; offer: boolean }) =>
      api.post(`/subscription-projects/${projectId}/${offer ? 'enable' : 'disable'}`),
    onSuccess: async (_r, { offer }) => {
      void queryClient.invalidateQueries({ queryKey: ['subscription-packages'] });
      await queryClient.invalidateQueries({ queryKey: ['subscription-projects'] });
      // Stopped, it leaves the list: show the first one still offered.
      if (!offer) setParams({});
      toast.success(offer ? t('pkgs.offeredDone') : t('pkgs.stoppedDone'));
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError && e.code === 'SUBSCRIPTIONS_RUNNING' ? t('pkgs.stillRunning', { count: (e.details as { live: number }).live }) : e instanceof Error ? e.message : t('common.error')),
  });
  const manage = can(P.SUBSCRIPTIONS_MANAGE);

  return (
    <>
      <PageHeader
        title={t('pkgs.title')}
        subtitle={t('pkgs.subtitle')}
        action={
          manage &&
          offerable && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/subscription-packages/offer')}>
              {t('pkgs.offerProject')}
            </Button>
          )
        }
      />
      {projects.isLoading ? (
        <LoadingBlock />
      ) : projects.error ? (
        <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
      ) : !projects.data?.length ? (
        <Card>
          <EmptyState icon={<Package className="h-8 w-8" />} title={t('pkgs.noProjects')} hint={t('pkgs.noProjectsHint')} />
        </Card>
      ) : !offered.length && !selected ? (
        <Card>
          <EmptyState icon={<Package className="h-8 w-8" />} title={t('pkgs.noneOffered')} hint={t('pkgs.noneOfferedHint')} />
        </Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]">
          <Card padded={false}>
            <CardHeader title={t('pkgs.products')} subtitle={t('pkgs.productsHint')} className="p-5 pb-2" />
            {!offered.length && <p className="px-5 pb-5 text-body-sm text-muted">{t('pkgs.noneOffered')}</p>}
            <ul className="divide-y divide-[var(--line)]">
              {offered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setParams({ projectId: p.id })}
                    className={`flex w-full items-start justify-between gap-3 px-5 py-3 text-start transition-colors duration-fast ${
                      p.id === selectedId ? 'bg-primary/5' : 'hover:bg-[var(--surface-sunken)]'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-body-sm font-medium">{localized(p)}</span>
                      <span className="block text-caption text-muted">
                        {p.code}
                        {p.closed ? ` · ${projectStatusLabel(p.status, locale)}` : ''}
                      </span>
                    </span>
                    {p.liveSubscriptions > 0 && (
                      <span className="shrink-0 text-caption text-muted">{t('pkgs.running', { count: p.liveSubscriptions })}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <div className="grid content-start gap-5">
            {!selected ? (
              <Card>
                <EmptyState title={t('pkgs.chooseProject')} hint="" />
              </Card>
            ) : (
              <Card padded={false}>
                <CardHeader
                  title={localized(selected)}
                  subtitle={selected.offered ? t('pkgs.offeredHint') : t('pkgs.notOfferedHint')}
                  className="p-5 pb-0"
                  action={
                    manage && (
                      <div className="flex flex-wrap gap-2">
                        {selected.offered ? (
                          <>
                            <Button
                              variant="primary"
                              icon={<Plus className="h-4 w-4" />}
                              onClick={() => navigate(`/subscription-packages/new?projectId=${selected.id}`)}
                            >
                              {t('pkgs.new')}
                            </Button>
                            <Button loading={toggle.isPending} onClick={() => toggle.mutate({ projectId: selected.id, offer: false })}>
                              {t('pkgs.stopOffering')}
                            </Button>
                          </>
                        ) : (
                          !selected.closed && (
                            <Button variant="primary" loading={toggle.isPending} onClick={() => toggle.mutate({ projectId: selected.id, offer: true })}>
                              {t('pkgs.offer')}
                            </Button>
                          )
                        )}
                      </div>
                    )
                  }
                />
                <div className="px-5 pt-3">
                  <Checkbox checked={archived} onChange={(e) => setArchived(e.target.checked)} label={t('pkgs.showArchived')} />
                </div>
                {packages.isLoading ? (
                  <LoadingBlock />
                ) : !packages.data?.length ? (
                  <EmptyState icon={<Package className="h-8 w-8" />} title={t('pkgs.none')} hint={selected.offered ? t('pkgs.noneHint') : t('pkgs.notOfferedHint')} />
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('pkgs.code')}</Th>
                        <Th>{t('pkgs.name')}</Th>
                        <Th align="end">{t('pkgs.listPrices')}</Th>
                        <Th align="end">{t('pkgs.subscribers')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.data.map((pkg) => (
                        <Tr key={pkg.id} onClick={() => navigate(`/subscription-packages/${pkg.id}`)}>
                          <Td className="font-mono text-mono-sm">{pkg.code}</Td>
                          <Td>
                            {localized(pkg)}
                            {!pkg.isActive && <Badge tone="neutral" className="ms-2">{t('pkgs.archived')}</Badge>}
                          </Td>
                          <Td align="end">
                            <ListPrices pkg={pkg} />
                          </Td>
                          <Td align="end" className="tabular">{pkg.liveSubscriptions}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** Chooses one more internal project to sell; it joins the Packages list. */
export function OfferProjectPage() {
  const { t, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const projects = useSubscriptionProjects();
  const [projectId, setProjectId] = useState('');
  const choices = (projects.data ?? []).filter((p) => !p.offered && !p.closed);

  const offer = useMutation({
    mutationFn: () => api.post(`/subscription-projects/${projectId}/enable`),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: ['subscription-packages'] });
      // Refetched before leaving, so the list it lands on already holds it.
      await queryClient.invalidateQueries({ queryKey: ['subscription-projects'] });
      toast.success(t('pkgs.offeredDone'));
      navigate(`/subscription-packages?projectId=${projectId}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  return (
    <FormPage
      title={t('pkgs.offerProject')}
      subtitle={t('pkgs.offerProjectHint')}
      backTo="/subscription-packages"
      backLabel={t('pkgs.title')}
      submitLabel={t('pkgs.offer')}
      onSubmit={() => offer.mutate()}
      submitting={offer.isPending}
      disabled={!projectId}
      loading={projects.isLoading}
      error={projects.error}
      onRetry={() => void projects.refetch()}
    >
      {choices.length === 0 ? (
        <p className="text-body-sm text-muted">{t('pkgs.nothingToOffer')}</p>
      ) : (
        <FormGrid>
          <Field label={t('pkgs.project')} hint={t('pkgs.projectHint')} required className="sm:col-span-2">
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">{t('pkgs.pickProject')}</option>
              {choices.map((p) => (
                <option key={p.id} value={p.id}>{p.code} — {localized(p)}</option>
              ))}
            </Select>
          </Field>
        </FormGrid>
      )}
    </FormPage>
  );
}

export function PackageDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const clientName = useClientName();
  const priceOf = usePeriodPrice();
  const { data: pkg, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-package', id],
    queryFn: () => api.get<PackageDetail>(`/subscription-packages/${id}`),
  });
  const restore = useMutation({
    mutationFn: () => api.post(`/subscription-packages/${id}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscription-package', id] });
      void queryClient.invalidateQueries({ queryKey: ['subscription-packages'] });
      toast.success(t('pkgs.restored'));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !pkg) return <ErrorState error={error} onRetry={() => void refetch()} />;
  const manage = can(P.SUBSCRIPTIONS_MANAGE);

  return (
    <>
      <div className="mb-4">
        <Link
          to={`/subscription-packages?projectId=${pkg.projectId}`}
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {localized(pkg.project)}
        </Link>
      </div>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{pkg.code}</span>
            <span>{localized(pkg)}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={pkg.isActive ? 'success' : 'neutral'}>{pkg.isActive ? t('pkgs.onSale') : t('pkgs.archived')}</Badge>
            <Badge tone="info">{pkg.currency}</Badge>
          </span>
        }
        action={
          manage && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate(`/subscription-packages/${id}/edit`)}>{t('common.edit')}</Button>
              {pkg.can.archive && (
                <Button variant="danger" onClick={() => navigate(`/subscription-packages/${id}/archive`)}>
                  {t('pkgs.archive')}
                </Button>
              )}
              {pkg.can.restore && (
                <Button loading={restore.isPending} onClick={() => restore.mutate()}>
                  {t('pkgs.restore')}
                </Button>
              )}
            </div>
          )
        }
      />
      <div className="grid gap-5 lg:grid-cols-3">
        <Card padded={false} className="lg:col-span-2">
          <CardHeader title={t('pkgs.subscribers')} className="p-5 pb-0" />
          {pkg.subscribers.length === 0 ? (
            <p className="px-5 py-4 text-body-sm text-muted">{t('pkgs.noSubscribers')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('subs.number')}</Th>
                  <Th>{t('subs.client')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('subs.netPrice')}</Th>
                </tr>
              </thead>
              <tbody>
                {pkg.subscribers.map((s) => (
                  <Tr key={s.id} onClick={() => navigate(`/subscriptions/${s.id}`)}>
                    <Td className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{s.number}</Td>
                    <Td>{clientName(s.client)}</Td>
                    <Td>
                      <SubscriptionStatusBadge status={s.status} />
                    </Td>
                    <Td align="end" className="whitespace-nowrap">{priceOf(s)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <Card>
          <CardHeader title={t('pkgs.listPrices')} subtitle={t('pkgs.listPriceNote')} />
          <dl className="divide-y divide-[var(--line)]">
            <Detail label={t('pkgs.monthlyPrice')}>
              {pkg.monthlyPrice === null ? t('pkgs.notSold') : formatMoney(pkg.monthlyPrice, pkg.currency, locale)}
            </Detail>
            <Detail label={t('pkgs.yearlyPrice')}>
              {pkg.yearlyPrice === null ? t('pkgs.notSold') : formatMoney(pkg.yearlyPrice, pkg.currency, locale)}
            </Detail>
            <Detail label={t('pkgs.everSubscribed')}>{pkg.everSubscribed}</Detail>
          </dl>
          {pkg.description && <p dir="auto" className="mt-3 whitespace-pre-wrap text-start text-body-sm text-muted">{pkg.description}</p>}
        </Card>
      </div>
    </>
  );
}

export function PackageFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const currencies = useCurrencies();
  const projects = useSubscriptionProjects();
  const existing = useQuery({
    queryKey: ['subscription-package', id],
    queryFn: () => api.get<PackageDetail>(`/subscription-packages/${id}`),
    enabled: editing,
  });

  const [projectId, setProjectId] = useState(params.get('projectId') ?? '');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [monthly, setMonthly] = useState('');
  const [yearly, setYearly] = useState('');

  useEffect(() => {
    const pkg = existing.data;
    if (!pkg) return;
    setProjectId(pkg.projectId);
    setCode(pkg.code);
    setName(pkg.name);
    setNameAr(pkg.nameAr ?? '');
    setDescription(pkg.description ?? '');
    setCurrency(pkg.currency);
    setMonthly(pkg.monthlyPrice === null ? '' : String(pkg.monthlyPrice));
    setYearly(pkg.yearlyPrice === null ? '' : String(pkg.yearlyPrice));
  }, [existing.data]);

  const price = (text: string) => (text.trim() === '' ? null : Number(text));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: code.trim(),
        name: name.trim(),
        nameAr: nameAr.trim() || null,
        description: description.trim() || null,
        currency,
        monthlyPrice: price(monthly),
        yearlyPrice: price(yearly),
      };
      return editing
        ? api.patch<PackageDetail>(`/subscription-packages/${id}`, body)
        : api.post<PackageDetail>('/subscription-packages', { ...body, projectId });
    },
    onSuccess: (pkg) => {
      void queryClient.invalidateQueries({ queryKey: ['subscription-packages'] });
      void queryClient.invalidateQueries({ queryKey: ['subscription-package', pkg.id] });
      void queryClient.invalidateQueries({ queryKey: ['subscription-projects'] });
      toast.success(editing ? t('pkgs.saved') : t('pkgs.created'));
      navigate(`/subscription-packages/${pkg.id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const fieldError = (field: string) =>
    save.error instanceof ApiError && (save.error.details as { field?: string } | undefined)?.field === field ? save.error.message : null;
  const can = existing.data?.can;
  const project = projects.data?.find((p) => p.id === projectId);

  return (
    <FormPage
      title={editing ? t('pkgs.editTitle', { name: existing.data?.name ?? '' }) : t('pkgs.new')}
      subtitle={project ? localized(project) : undefined}
      backLabel={t('pkgs.title')}
      submitLabel={t('common.save')}
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!name.trim() || !code.trim() || !projectId || (price(monthly) === null && price(yearly) === null)}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
    >
      <FormGrid>
        {!editing && (
          <Field label={t('subs.product')} required error={fieldError('projectId')} className="sm:col-span-2">
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">{t('subs.form.chooseProduct')}</option>
              {(projects.data ?? []).filter((p) => p.offered).map((p) => (
                <option key={p.id} value={p.id}>{p.code} — {localized(p)}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t('pkgs.name')} required error={fieldError('name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} dir="auto" />
        </Field>
        <Field label={t('pkgs.nameAr')} error={fieldError('nameAr')}>
          <Input value={nameAr} onChange={(e) => setNameAr(e.target.value)} maxLength={120} dir="rtl" lang="ar" />
        </Field>
        <Field label={t('pkgs.code')} hint={t('pkgs.codeHint')} required error={fieldError('code')}>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={32}
            dir="ltr"
            disabled={editing && can?.changeCode === false}
          />
        </Field>
        <Field label={t('pkgs.currency')} hint={t('pkgs.currencyHint')} required error={fieldError('currency')}>
          <Select value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={editing && can?.changeCurrency === false}>
            {(currencies.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('pkgs.monthlyPrice')} hint={t('pkgs.priceHint')} error={fieldError('monthlyPrice')}>
          <Input type="number" min={0} step="0.01" inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value)} dir="ltr" />
        </Field>
        <Field label={t('pkgs.yearlyPrice')} hint={t('pkgs.priceHint')} error={fieldError('yearlyPrice')}>
          <Input type="number" min={0} step="0.01" inputMode="decimal" value={yearly} onChange={(e) => setYearly(e.target.value)} dir="ltr" />
        </Field>
        <Field label={t('pkgs.description')} className="sm:col-span-2">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} dir="auto" />
        </Field>
      </FormGrid>
      {editing && <p className="mt-4 text-body-sm text-muted">{t('pkgs.listPriceNote')}</p>}
    </FormPage>
  );
}

export function PackageArchivePage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: pkg, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-package', id],
    queryFn: () => api.get<PackageDetail>(`/subscription-packages/${id}`),
  });
  const archive = useMutation({
    mutationFn: () => api.post(`/subscription-packages/${id}/archive`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscription-package', id] });
      void queryClient.invalidateQueries({ queryKey: ['subscription-packages'] });
      toast.success(t('pkgs.archivedDone'));
      navigate(`/subscription-packages/${id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  return (
    <FormPage
      title={t('pkgs.archiveTitle', { name: pkg ? localized(pkg) : '' })}
      backTo={`/subscription-packages/${id}`}
      backLabel={pkg?.code ?? t('pkgs.title')}
      submitLabel={t('pkgs.archive')}
      onSubmit={() => archive.mutate()}
      submitting={archive.isPending}
      destructive
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      <p className="text-body">
        {pkg && pkg.liveSubscriptions > 0 ? t('pkgs.archiveHint', { count: pkg.liveSubscriptions }) : t('pkgs.archiveHintNone')}
      </p>
    </FormPage>
  );
}
